import { useAgenticLogicRuleVersions } from "@src/api/agentic-logic/get-agentic-logic-rule-versions";
import { useAgenticLogicAssignments } from "@src/api/agentic-logic/list-agentic-logic-assignments";

import {
  resolveRuleSaveAction,
  ruleLiveness,
  type RuleLiveness,
  type RuleSaveAction,
} from "./publishOnSave";

/**
 * Is this rule in front of shoppers, and what may Save do about it.
 *
 * The rule page already reads the org's assignments — the same list that feeds
 * the byline and the split button's "Save to all N experiences" — and already
 * reads this rule's versions, for the Versions tab and the unpublished-changes
 * callout. So this adds no request: TanStack answers every observer of a key
 * from one cache entry.
 *
 * An error is reported as `unknown`, not as "attached to nothing". Reading a
 * failed join as an unattached rule would hand the merchant back the very
 * auto-publish this exists to withhold, on the one page where nobody can see
 * that the read failed.
 *
 * The version read is what keeps that caution from becoming a trap: a rule with
 * no active version has never reached a shopper, and it is also the one rule
 * with no publish affordance anywhere else on the page, so an unknown liveness
 * there must not swallow the deploy. `resolveRuleSaveAction` owns that rule;
 * this hook's job is to hand it an honest tri-state — `undefined` until the
 * version read has actually answered, never a `false` that merely means "not
 * loaded".
 */
export const useRuleLiveness = (
  ruleId: string
): { liveness: RuleLiveness; saveAction: RuleSaveAction } => {
  const assignmentsQuery = useAgenticLogicAssignments();
  const assignments = assignmentsQuery.isError
    ? undefined
    : assignmentsQuery.data?.assignments;

  const versionsQuery = useAgenticLogicRuleVersions(ruleId);
  const hasActiveVersion = versionsQuery.isSuccess
    ? versionsQuery.data.versions.some((version) => version.status === "active")
    : undefined;

  return {
    liveness: ruleLiveness({ ruleId, assignments }),
    saveAction: resolveRuleSaveAction({
      ruleId,
      assignments,
      hasActiveVersion,
    }),
  };
};
